import { P as PubSub } from './pubsub-COBwjfLh.js';
import './notyf-DIw0RvBj.js';
import { f as fetchHTML } from './fetchHTML-CGNR3V1z.js';
import './parseHTML-Om2c1Ayj.js';

/**
 * @typedef {HTMLElement & {
 *   open: () => void,
 *   close: () => void,
 *   controllers: HTMLElement[],
 * }} DrawerElement
 */

class QuickShop {
  /** @type {Map<string, QuickShop>} */
  static #instances = new Map();
  /** @type {Map<string, Promise<QuickShop>>} */
  static #pending = new Map();

  /** @type {DrawerElement} */ #drawer;
  /** @type {() => void} */ #nativeClose;
  /** @type {() => void} */ #nativeOpen;

  /**
   * @param {DrawerElement} drawer
   */
  constructor(drawer) {
    this.#drawer = drawer;
    this.#nativeClose = drawer.close.bind(drawer);
    this.#nativeOpen = drawer.open.bind(drawer);
    QuickShop.#instances.set(drawer.id, this);

    // Focus-trap can hide the dialog without calling close() — keep trigger state in sync.
    new MutationObserver(() => {
      if (this.#drawer.getAttribute('aria-hidden') !== 'true') return;
      this.#setTriggersExpanded(false);
    }).observe(this.#drawer, { attributes: true, attributeFilter: ['aria-hidden'] });

    PubSub.subscribe('cart:add', () => this.close());
    PubSub.subscribe('key:escape', () => this.close());
  }

  /** @param {string} id */
  static get(id) {
    return QuickShop.#instances.get(id) ?? null;
  }

  /**
   * Fetch and mount a `drawer-element` for quick shop.
   * Concurrent opens for the same id reuse the in-flight promise.
   * @param {string} url
   * @param {string} id
   * @returns {Promise<QuickShop>}
   */
  static createFromUrl(url, id) {
    const pending = QuickShop.#pending.get(id);
    if (pending) return pending;

    const promise = QuickShop.#mount(url, id).finally(() => QuickShop.#pending.delete(id));
    QuickShop.#pending.set(id, promise);
    return promise;
  }

  /**
   * @param {string} url
   * @param {string} id
   * @returns {Promise<QuickShop>}
   */
  static async #mount(url, id) {
    const fetchUrl = new URL(url, window.location.origin);
    // Alternate template only — `section_id=fetch-quick-shop` renders the
    // section file with schema defaults, not product.quick-shop.json settings.
    fetchUrl.searchParams.set('view', 'quick-shop');

    const html = await fetchHTML(fetchUrl.toString());
    const source = html.querySelector('drawer-element');
    if (!source?.id) throw new Error('Quick shop drawer missing from response');

    document.body.insertAdjacentHTML('afterbegin', source.outerHTML);

    const drawer = /** @type {DrawerElement | null} */ (document.getElementById(id));
    if (!drawer) throw new Error(`Quick shop drawer "${id}" failed to mount`);

    return new QuickShop(drawer);
  }

  /** @returns {boolean} */
  isOpen() {
    return this.#drawer.getAttribute('aria-hidden') === 'false';
  }

  /** @returns {void} */
  open() {
    this.#nativeOpen();
    this.#setTriggersExpanded(true);
  }

  /** @returns {void} */
  close() {
    this.#nativeClose();
    this.#setTriggersExpanded(false);
  }

  /** @param {boolean} expanded */
  #setTriggersExpanded(expanded) {
    document
      .querySelectorAll(`[data-quick-shop-id="${CSS.escape(this.#drawer.id)}"]`)
      .forEach((trigger) => trigger.setAttribute('aria-expanded', String(expanded)));
  }
}

/**
 * Trigger button for Liquid + Algolia cards. Owns open/create so InstantSearch re-renders
 * just reconnect a fresh instance — no document-level click delegation.
 */
class QuickShopButton extends HTMLElement {
  /** @param {HTMLElement} trigger */
  static #trackQuickViewAttribution(trigger) {
    const card = trigger.closest('[data-algolia-object-id]');
    if (!(card instanceof HTMLElement)) return;

    window.AlgoliaInsights?.trackQuickViewAttribution?.(card);
  }

  /** @param {QuickShop} shop @param {HTMLElement} trigger */
  static #openQuickShop(shop, trigger) {
    QuickShopButton.#trackQuickViewAttribution(trigger);
    shop.open();
  }

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.click();
  };

  #onClick = async (event) => {
    event.preventDefault();
    if (this.classList.contains('loading')) return;

    const id = this.dataset.quickShopId;
    const url = this.dataset.quickShopUrl;
    if (!id || !url) return;

    // Add loading class
    this.classList.add('loading');

    // Open existing quick shop
    const existing = QuickShop.get(id);
    if (existing) {
      if (existing.isOpen()) {
        existing.close();
      } else {
        QuickShopButton.#openQuickShop(existing, this);
      }
      this.classList.remove('loading');
      return;
    }

    // Create new quick shop
    try {
      const shop = await QuickShop.createFromUrl(url, id);
      QuickShopButton.#openQuickShop(shop, this);
    } catch (error) {
      console.error('Error opening quick shop:', error);
    } finally {
      this.classList.remove('loading');
    }
  };
}

customElements.define('quick-shop-button', QuickShopButton);
